import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { addDays, subDays, format } from 'date-fns'
import type { TimeRangeOption } from '@/types/analytics'

export { type TimeRangeOption };

interface SiteAnalyticsData {
  totalVisits: number
  uniqueVisitors: number
  avgSessionDuration: number
  bounceRate: number
  topPages: Array<{
    page_path: string
    page_title: string | null
    page_views: number
    unique_visitors: number
    avg_time_on_page: number
  }>
  trafficSources: Array<{
    source_name: string
    source_type: string
    visit_count: number
    unique_visitors: number
  }>
  deviceBreakdown: Array<{
    device_type: string
    count: number
    percentage: number
  }>
  browserBreakdown: Array<{
    browser_type: string
    count: number
    percentage: number
  }>
  countryBreakdown: Array<{
    country: string
    count: number
    percentage: number
  }>
  dailyVisits: Array<{
    date: string
    visits: number
    unique_visitors: number
  }>
  realTimeVisitors: number
}

const getDateRange = (timeRange: TimeRangeOption) => {
  const now = new Date()
  let startDate: Date
  
  switch (timeRange) {
    case '24h':
      startDate = subDays(now, 1)
      break
    case '7days':
      startDate = subDays(now, 7)
      break
    case '30days':
      startDate = subDays(now, 30)
      break
    case '90days':
      startDate = subDays(now, 90)
      break
    default:
      startDate = subDays(now, 7)
  }
  
  return { startDate, endDate: now }
}

export const useSiteAnalytics = (timeRange: TimeRangeOption = '7days') => {
  return useQuery({
    queryKey: ['siteAnalytics', timeRange],
    queryFn: async (): Promise<SiteAnalyticsData> => {
      try {
        const { startDate, endDate } = getDateRange(timeRange)
        
        // Use the edge function to get analytics data
        const { data, error } = await supabase.functions.invoke('get-site-analytics', {
          body: {
            timeRange,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }
        })

        if (error) {
          console.error('Analytics fetch error:', error)
          throw error
        }

        return data || {
          totalVisits: 0,
          uniqueVisitors: 0,
          avgSessionDuration: 0,
          bounceRate: 0,
          topPages: [],
        trafficSources: [],
        deviceBreakdown: [],
        browserBreakdown: [],
        countryBreakdown: [],
        dailyVisits: [],
        realTimeVisitors: 0
        }
      } catch (error) {
        console.error('Site analytics error:', error)
        
        // Return default empty data structure if there's an error
        return {
          totalVisits: 0,
          uniqueVisitors: 0,
          avgSessionDuration: 0,
          bounceRate: 0,
          topPages: [],
          trafficSources: [],
          deviceBreakdown: [],
          browserBreakdown: [],
          countryBreakdown: [],
          dailyVisits: [],
          realTimeVisitors: 0
        }
      }
    },
    refetchInterval: 60000, // Refresh every minute for real-time data
  })
}
